import React, { useCallback, useState, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { styled } from "styled-components";
import { colors } from "../theme/colors";
import { spacing } from "../theme/spacing";

const CarouselWrapper = styled.div`
  margin: auto;
  width: 100%;
  --slide-spacing: 1rem;
  --slide-size: 80%;
`;

const CarouselViewport = styled.div`
  overflow: hidden;
`;

const Container = styled.div`
  display: flex;
  touch-action: pan-y pinch-zoom;
  margin-left: calc(var(--slide-spacing) * -1);
`;

const Slide = styled.div`
  background-color: ${colors["$white"]};
  border: 1px solid ${colors["$black"]};
  overflow: hidden;
  border-radius: 12px;
  transform: translate3d(0, 0, 0);
  flex: 0 0 var(--slide-size);
  min-width: 0;
  margin-left: var(--slide-spacing);
`;

const NavigationElements = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: ${spacing["4"]};
`;

const Button = styled.button`
  background-color: transparent;
  fill: ${colors["$black"]};
  width: 48px;
  height: 48px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
    fill: ${colors["$black"]};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${spacing["10"]};
`;

const CarouselComponent = ({ children = [] }) => {
  const isBrowser = typeof window !== "undefined";

  const [emblaRef, emblaApi] = useEmblaCarousel(
    isBrowser ? { loop: true } : undefined,
  );
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const [selectedSnap, setSelectedSnap] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  const updateScrollSnapState = useCallback((emblaApi) => {
    setSnapCount(emblaApi.scrollSnapList().length);
    setSelectedSnap(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    updateScrollSnapState(emblaApi);
    emblaApi.on("select", updateScrollSnapState);
    emblaApi.on("reInit", updateScrollSnapState);
  }, [emblaApi, updateScrollSnapState]);

  return (
    <CarouselWrapper className="embla">
      <CarouselViewport className="embla__viewport" ref={emblaRef}>
        <Container className="embla__container">
          {React.Children.map(children, (child, index) => (
            <Slide className="embla__slide" key={index}>
              {child}
            </Slide>
          ))}
        </Container>
      </CarouselViewport>
      <NavigationElements>
        <ButtonContainer>
          <Button className="embla__prev" onClick={scrollPrev}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 45 35"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M16 0L19.456 3.136L8.256 15.04L44.224 15.04L44.224 19.52L8.256 19.52L19.456 31.424L16 34.56L0 17.28L16 0Z" />
            </svg>
          </Button>
          <Button className="embla__next" onClick={scrollNext}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 45 35"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M28.224 34.56L24.768 31.424L35.968 19.52H0V15.04H35.968L24.768 3.136L28.224 0L44.224 17.28L28.224 34.56Z" />
            </svg>
          </Button>
        </ButtonContainer>
        <p>
          {selectedSnap + 1} / {snapCount}
        </p>
      </NavigationElements>
    </CarouselWrapper>
  );
};

export default CarouselComponent;
